"use client"

import { useState, useEffect, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { GoogleLogin, useGoogleLogin } from '@react-oauth/google'
import AppleLogin from 'react-apple-login'
import { jwtDecode } from 'jwt-decode'
import { useAuth } from "../../hooks/useAuth"
import OTPModal from "../../components/auth/OTPModal" // Assuming path is correct
import SuccessModal from "../../components/auth/SuccessModal" // Assuming path is correct
import { useTranslation } from "react-i18next";
import { USER_TYPES } from "../../constants/userTypes"

// Registration flow states
const REGISTRATION_STATES = {
  FORM_FILLING: "form_filling",
  SUBMITTING: "submitting",
  OTP_PENDING: "otp_pending",
  OTP_VERIFYING: "otp_verifying",
  OTP_ERROR: "otp_error",
  OTP_EXPIRED: "otp_expired",
  OTP_RESENDING: "otp_resending",
  VERIFICATION_SUCCESS: "verification_success",
  COMPLETING_REGISTRATION: "completing_registration",
  ERROR: "error",
}

const Register = () => {
  const { userType } = useParams()
  const navigate = useNavigate()
  const { login } = useAuth()
  const { t, i18n } = useTranslation()
  const isRTL = i18n.dir && i18n.dir() === "rtl"
  const supportedUserTypes = ["pet_guardian", "veterinarian", "service_provider", "firm"]
  const normalizedUserType = supportedUserTypes.includes(userType) ? userType : "pet_guardian"

  // Main registration state
  const [registrationState, setRegistrationState] = useState(REGISTRATION_STATES.FORM_FILLING)

  // Form data
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    mobile: "",
    userType: normalizedUserType.toUpperCase(),
  })

  // UI state
  const [activeTab, setActiveTab] = useState("email")
  const [errors, setErrors] = useState({})
  const [successMessage, setSuccessMessage] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  // OTP state
  const [otpData, setOtpData] = useState({
    verificationId: null,
    target: "",
    sessionData: null,
    expiresAt: null,
    attemptsRemaining: 3,
    canResend: false,
    nextResendTime: null,
  })

  // Countdown timer state
  const [timeRemaining, setTimeRemaining] = useState(0)

  // User type configurations
  const userTypeConfig = {
    pet_guardian: {
      title: t("register.roles.pet_guardian.title"),
      subtitle: t("register.roles.pet_guardian.subtitle"),
      alt: t("register.roles.pet_guardian.alt"),
      primaryColor: "#8548A8",
      mainCircleGradient: "linear-gradient(135deg, #8548A8 0%, #2A8AD2 100%)",
      floatingCircle1: "linear-gradient(135deg, #FFA055 0%, #8548A8 100%)",
      floatingCircle2: "#8548A8",
      floatingCircle3: "linear-gradient(135deg, #FFA055 0%, #FF6B9D 100%)",
      floatingCircle4: "#2A8AD2",
      floatingCircle5: "rgba(255, 160, 85, 0.6)",
    },
    veterinarian: {
      title: t("register.roles.veterinarian.title"),
      subtitle: t("register.roles.veterinarian.subtitle"),
      alt: t("register.roles.veterinarian.alt"),
      primaryColor: "#00D4B3",
      mainCircleGradient: "linear-gradient(135deg, #2A8AD2 0%, #00D483 100%)",
      floatingCircle1: "linear-gradient(135deg, #00D4B3 0%, #2A8AD2 100%)",
      floatingCircle2: "#00D4B3",
      floatingCircle3: "linear-gradient(135deg, #2A8AD2 0%, #00D4B3 100%)",
      floatingCircle4: "#2A8AD2",
      floatingCircle5: "rgba(0, 212, 179, 0.6)",
    },
    service_provider: {
      title: t("register.roles.service_provider.title"),
      subtitle: t("register.roles.service_provider.subtitle"),
      alt: t("register.roles.service_provider.alt"),
      primaryColor: "#D65745",
      mainCircleGradient: "linear-gradient(135deg, #D65745 0%, #00D483 100%)",
      floatingCircle1: "linear-gradient(135deg, #D65745 0%, #00D4B3 100%)",
      floatingCircle2: "#D65745",
      floatingCircle3: "linear-gradient(135deg, #00D4B3 0%, #D65745 100%)",
      floatingCircle4: "#00D4B3",
      floatingCircle5: "rgba(214, 87, 69, 0.6)",
    },
    firm: {
      title: t("register.roles.firm.title"),
      subtitle: t("register.roles.firm.subtitle"),
      alt: t("register.roles.firm.alt"),
      primaryColor: "#000080",
      mainCircleGradient: "linear-gradient(135deg, #000080 0%, #2A8AD2 100%)",
      floatingCircle1: "linear-gradient(135deg, #000080 0%, #FFA055 100%)",
      floatingCircle2: "#000080",
      floatingCircle3: "linear-gradient(135deg, #2A8AD2 0%, #000080 100%)",
      floatingCircle4: "#2A8AD2",
      floatingCircle5: "rgba(0, 0, 128, 0.6)",
    },
  }

  const config = userTypeConfig[normalizedUserType]

  useEffect(() => {
    let interval

    if (
      timeRemaining > 0 &&
      (registrationState === REGISTRATION_STATES.OTP_PENDING || registrationState === REGISTRATION_STATES.OTP_ERROR)
    ) {
      interval = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            setRegistrationState(REGISTRATION_STATES.OTP_EXPIRED)
            setOtpData((prev) => ({ ...prev, canResend: true }))
            return 0
          }
          return prev - 1
        })
      }, 1000)
    } else {
      clearInterval(interval)
    }

    return () => clearInterval(interval)
  }, [timeRemaining, registrationState])

  // Auto-clear success messages
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(""), 3000)
      return () => clearTimeout(timer)
    }
  }, [successMessage])

  // Clear errors when user starts typing
  const clearFieldError = useCallback((fieldName) => {
    setErrors((prev) => {
      const newErrors = { ...prev }
      delete newErrors[fieldName]
      return newErrors
    })
  }, [])

  // Form validation
  const validateForm = useCallback(() => {
    const newErrors = {}

    if (!formData.firstName.trim()) {
      newErrors.firstName = t("register.errors.firstNameRequired")
    } else if (formData.firstName.trim().length < 2) {
      newErrors.firstName = t("register.errors.firstNameLength")
    }

    if (!formData.lastName.trim() && normalizedUserType !== "firm") {
      newErrors.lastName = t("register.errors.lastNameRequired")
    } else if (formData.lastName.trim() && formData.lastName.trim().length < 2) {
      newErrors.lastName = t("register.errors.lastNameLength")
    }

    if (activeTab === "email") {
      if (!formData.email.trim()) {
        newErrors.email = t("register.errors.emailRequired")
      } else {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
        if (!emailRegex.test(formData.email)) {
          newErrors.email = t("register.errors.emailInvalid")
        }
      }
    } else {
      if (!formData.mobile.trim()) {
        newErrors.mobile = t("register.errors.mobileRequired")
      } else if (formData.mobile.length < 10) {
        newErrors.mobile = t("register.errors.mobileInvalid")
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }, [formData, activeTab, t])

  // Handle input changes
  const handleInputChange = useCallback(
    (e) => {
      const { name, value } = e.target
      let finalValue = value
      if (name === "mobile") {
        finalValue = value.replace(/[^0-9\+\-\s]/g, "")
      }
      setFormData((prev) => ({ ...prev, [name]: finalValue }))
      // Clear specific field error
      if (errors[name]) {
        setErrors((prev) => {
          const newErrors = { ...prev }
          delete newErrors[name]
          return newErrors
        })
      }
    },
    [errors],
  )

  // Handle tab change
  const handleTabChange = useCallback((tab) => {
    setActiveTab(tab)
    setErrors({})
  }, [])

  // Error handling
  const handleApiError = useCallback((status, data) => {
    setRegistrationState(REGISTRATION_STATES.ERROR)

    if (status === 400) {
      // Handle the new response format with detailed error information
      if (data.errors?.error_type === "duplicate_registration") {
        const field = data.errors.field
        const contactInfo = data.errors.contact_info
        const duplicateKey = field === "phone" ? "duplicatePhone" : "duplicateEmail"
        setErrors({
          general: t(`register.errors.${duplicateKey}`, { contactInfo }),
        })
      } else if (data.message?.includes("Email already registered")) {
        setErrors({ email: t("register.errors.emailDuplicate") })
      } else if (data.message?.includes("Phone already registered")) {
        setErrors({ mobile: t("register.errors.mobileDuplicate") })
      } else {
        setErrors({ general: data.message || t("register.errors.registrationFailed") })
      }
    } else if (status === 422) {
      setErrors({ general: t("register.errors.checkInformation") })
    } else if (status === 429) {
      setErrors({ general: t("register.errors.tooManyAttempts") })
    } else {
      setErrors({ general: data.message || t("register.errors.registrationFailed") })
    }
  }, [t])

  // Handle Google OAuth success
  const handleGoogleSuccess = useCallback(async (credentialResponse) => {
    try {
      setIsLoading(true)
      setErrors({})


      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      // Send the credential to your backend
      const response = await fetch(`${API_BASE_URL}/api/v1/auth/google/verify-registration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          access_token: credentialResponse.access_token,
          user_type: normalizedUserType.toUpperCase()
        }),
      })

      const data = await response.json()

      if (response.ok) {
        // Handle the response based on the actual backend structure
        const responseData = data.data || data

        if (responseData.user) {
          // Store user data and token
          localStorage.setItem('access_token', responseData.access_token)
          localStorage.setItem('user_data', JSON.stringify(responseData.user))

          // Authenticate the user in the auth context
          try {
            // Set authentication state manually since we have the token and user data
            // This bypasses the login function but sets the auth state properly
            const authEvent = new CustomEvent('auth-success', {
              detail: {
                user: responseData.user,
                token: responseData.access_token,
                method: 'google'
              }
            })
            window.dispatchEvent(authEvent)
          } catch (error) {
            console.error('Error setting auth state:', error)
          }

          if (responseData.is_new_user) {
            // New user - redirect to Google registration
            navigate(`/auth/complete-profile/${normalizedUserType}`, {
              state: {
                googleData: responseData.user,
                access_token: responseData.access_token
              }
            })
          } else {
            // Check if user needs profile completion
            if (responseData.user.profile_status == "incomplete") {
              // Redirect to profile completion
              navigate(`/auth/complete-profile/${normalizedUserType}`, {
                state: {
                  user: responseData.user,
                  token: responseData.access_token,
                  loginMethod: 'google'
                }
              })
            } else {
              // Existing user with complete profile - navigate to dashboard
              const userType = responseData.user.user_type || responseData.user.userType || responseData.user.type
              let redirectRoute = '/social'

              // ADMIN users always go to admin dashboard
              if (userType === USER_TYPES.ADMIN) {
                redirectRoute = '/admin/dashboard'
              } else if (userType === USER_TYPES.CLINIC_MODERATOR) {
                redirectRoute = '/medical/clinic/appointment-management/appointments'
              } else {
                redirectRoute = localStorage.getItem('google_login_intended_route') || '/social'
              }

              localStorage.removeItem('google_login_intended_route')
              localStorage.removeItem('google_login_user_type')

              navigate(redirectRoute, {
                state: {
                  loginSuccess: true,
                  user: responseData.user,
                  loginMethod: 'google'
                }
              })
            }
          }

        } else {
          setErrors({ general: data.message || t("register.errors.googleFailed") })
        }
      } else {
        setErrors({ general: data.message || t("register.errors.googleFailed") })
      }
    } catch (error) {
      console.error('Google OAuth error:', error)
      setErrors({ general: t("register.errors.googleFailed") })
    } finally {
      setIsLoading(false)
    }
  }, [navigate, normalizedUserType, t])

  // Handle Google OAuth error
  const handleGoogleError = useCallback(() => {
    console.error('Google OAuth failed')
    setErrors({ general: t("register.errors.googleFailed") })
    setIsLoading(false)
  }, [t])

  // Handle Apple OAuth Success
  const handleAppleSuccess = useCallback(async (response) => {
    if (response.error) {
      console.error('Apple login error:', response.error)
      setErrors({ general: "Apple Sign-In failed" })
      return
    }

    try {
      setIsLoading(true)
      // response structure: { authorization: { id_token, code, ... }, user: { name: { firstName, ... }, email: ... } }
      const { authorization, user } = response

      if (!authorization?.id_token) {
        throw new Error("No identity token received")
      }

      const decodedToken = jwtDecode(authorization.id_token)
      const appleId = decodedToken.sub
      const email = user?.email || decodedToken.email

      // Prepare data for verification/login
      const appleData = {
        sub: appleId,
        email: email,
        given_name: user?.name?.firstName,
        family_name: user?.name?.lastName,
        id_token: authorization.id_token // Optional if backend wants to verify
      }

      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      // Verify if user exists
      const verifyResponse = await fetch(`${API_BASE_URL}/api/v1/auth/apple/verify-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          apple_id: appleId,
          email: email
        })
      })

      const json = await verifyResponse.json()
      // Handle both { data: { user: ... } } and { user: ... } structures
      const verifyData = json.data || json

      if (verifyResponse.ok && (verifyData.user || verifyData.token || verifyData.access_token)) {
        // User exists, log them in
        if (verifyData.token) {
          localStorage.setItem('access_token', verifyData.token)
        } else if (verifyData.access_token) {
          localStorage.setItem('access_token', verifyData.access_token)
        }
        if (verifyData.user) {
          localStorage.setItem('user_data', JSON.stringify(verifyData.user))
        }

        // Authenticate context
        try {
          if (verifyData.user) {
            const authEvent = new CustomEvent('auth-success', {
              detail: { user: verifyData.user, token: verifyData.access_token || verifyData.token, method: 'apple' }
            })
            window.dispatchEvent(authEvent)
          }
        } catch (e) {
          console.error("Auth event dispatch error:", e)
        }

        // Navigate
        if (verifyData.user) {
          const userType = verifyData.user.user_type || verifyData.user.type
          if (verifyData.user.profile_status === 'incomplete') {
            navigate(`/auth/complete-profile/${normalizedUserType}`, {
              state: { user: verifyData.user, loginMethod: 'apple' }
            })
          } else {
            // Redirect based on type
            let redirectRoute = '/social'
            if (userType === USER_TYPES.ADMIN) redirectRoute = '/admin/dashboard'
            else if (userType === USER_TYPES.CLINIC_MODERATOR) redirectRoute = '/medical/clinic/appointment-management/appointments'

            navigate(redirectRoute, { state: { loginSuccess: true, user: verifyData.user } })
          }
        } else {
          // Fallback if token exists but no user object (should ideally fetch user profile here if needed, or redirect)
          navigate('/social', { state: { loginSuccess: true } })
        }
      } else {
        // User not found, redirect to Apple Register
        navigate(`/auth/apple-register/${normalizedUserType}`, {
          state: {
            appleData: appleData
          }
        })
      }
    } catch (error) {
      console.error("Apple processing error details:", error)
      setErrors({ general: "Error processing Apple Sign-In" })
    } finally {
      setIsLoading(false)
    }
  }, [navigate, normalizedUserType, t])

  // Google OAuth hook - defined after the handler functions
  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  })

  // Social Login Handler
  const handleSocialLogin = useCallback(
    async (provider) => {
      setIsLoading(true)
      setErrors({})

      try {
        if (provider === "google") {
          // Initiate Google OAuth flow using the hook
          googleLogin()
        } else if (provider === "apple") {
          // Should ideally not be reached if button is replaced, but kept for safety or if other triggers exist
          console.warn("Apple login triggered via handleSocialLogin instead of direct component")
        } else {
          setErrors({ general: t("register.errors.unsupportedProvider") })
        }
      } catch (error) {
        console.error(`${provider} login failed:`, error)
        setErrors({
          general: t("register.errors.providerFailed", { provider }),
        })
      } finally {
        setIsLoading(false)
      }
    },
    [navigate, normalizedUserType, googleLogin, t],
  )

  // API calls
  const initiateRegistration = useCallback(async () => {
    if (!validateForm()) return

    setRegistrationState(REGISTRATION_STATES.SUBMITTING)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      const userTypeMapping = {
        pet_guardian: "PET_GUARDIAN",
        veterinarian: "VETERINARIAN",
        service_provider: "SERVICE_PROVIDER", // Backend uses SERVICE_PROVIDER, but UI shows "Service Provider"
        firm: "FIRM",
      }

      const backendUserType = userTypeMapping[normalizedUserType]

      if (!backendUserType) {
        throw new Error("Invalid user type selected")
      }

      const registrationData = {
        user_type: backendUserType,
        first_name: formData.firstName.trim(),
        ...(normalizedUserType !== "firm" && { last_name: formData.lastName.trim() }),
        ...(activeTab === "email" && { email: formData.email.trim() }),
        ...(activeTab === "mobile" && { phone: formData.mobile.trim() }),
        language_preference: i18n.language || "en",
      }


      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/initiate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(registrationData),
      })

      const data = await response.json()
      const responseData = data.data || data

      if (response.ok) {
        // Handle success...
        setOtpData({
          verificationId: responseData.verification_id,
          target: responseData.target,
          sessionData: responseData,
          expiresAt: new Date(Date.now() + responseData.expires_in_minutes * 60 * 1000),
          attemptsRemaining: 3,
          canResend: false,
          nextResendTime: new Date(Date.now() + 60000), // 1 minute
        })

        setTimeRemaining(responseData.expires_in_minutes * 60)
        setRegistrationState(REGISTRATION_STATES.OTP_PENDING)
        setErrors({})
      } else {
        // Handle API errors...
        handleApiError(response.status, data)
      }
    } catch (error) {
      console.error("Registration error:", error)
      setErrors({ general: t("register.errors.network") })
      setRegistrationState(REGISTRATION_STATES.ERROR)
    }
  }, [formData, activeTab, normalizedUserType, validateForm, handleApiError, i18n.language, t])

  const verifyOTP = useCallback(
    async (code) => {
      setRegistrationState(REGISTRATION_STATES.OTP_VERIFYING)

      try {
        const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

        const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/verify-otp`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            verification_id: otpData.verificationId,
            code: code,
          }),
        })

        const data = await response.json()

        if (response.ok) {
          localStorage.setItem("verification_id", otpData.verificationId)

          setRegistrationState(REGISTRATION_STATES.VERIFICATION_SUCCESS)

        } else {
          setOtpData((prev) => ({
            ...prev,
            attemptsRemaining: Math.max(0, prev.attemptsRemaining - 1),
          }))

          setErrors({ otp: data.detail || t("register.errors.otpInvalid") })
          setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
        }
      } catch (error) {
        console.error("OTP verification error:", error)
        setErrors({ otp: t("register.errors.otpNetwork") })
        setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
      }
    },
    [otpData.verificationId, t],
  )

  const resendOTP = useCallback(async () => {
    setRegistrationState(REGISTRATION_STATES.OTP_RESENDING)

    try {
      const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000"

      const response = await fetch(`${API_BASE_URL}/api/v1/auth/register/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          verification_id: otpData.verificationId,
        }),
      })

      const data = await response.json()

      if (response.ok) {
        const expiresAt = new Date(Date.now() + (data.expires_in_minutes || 10) * 60 * 1000)

        setOtpData((prev) => ({
          ...prev,
          verificationId: data.verification_id || prev.verificationId,
          expiresAt,
          attemptsRemaining: 3,
          canResend: false,
          nextResendTime: new Date(Date.now() + 60000),
        }))

        setTimeRemaining((data.expires_in_minutes || 10) * 60)
        setRegistrationState(REGISTRATION_STATES.OTP_PENDING)
        setSuccessMessage(t("register.messages.newCode"))
        setErrors({})

      } else {
        setErrors({ otp: data.detail || t("register.errors.otpResendFailed") })
        setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
      }
    } catch (error) {
      console.error("OTP resend error:", error)
      setErrors({ otp: t("register.errors.otpNetwork") })
      setRegistrationState(REGISTRATION_STATES.OTP_ERROR)
    }
  }, [otpData.verificationId, t])

  // Event handlers
  const handleSubmit = useCallback(() => {
    initiateRegistration()
  }, [initiateRegistration])

  const handleOTPVerify = useCallback(
    (code) => {
      verifyOTP(code)
    },
    [verifyOTP],
  )

  const handleOTPResend = useCallback(() => {
    resendOTP()
  }, [resendOTP])

  const handleSuccessContinue = useCallback(() => {
    setRegistrationState(REGISTRATION_STATES.COMPLETING_REGISTRATION)

    navigate(`/auth/create-password/${normalizedUserType}`, {
      state: {
        verificationId: otpData.verificationId,
        verificationComplete: true,
        userData: formData,
      },
    })
  }, [navigate, normalizedUserType, otpData.verificationId, formData])

  // Computed values
  const isFormLoading = registrationState === REGISTRATION_STATES.SUBMITTING || isLoading
  const showOTPModal = [
    REGISTRATION_STATES.OTP_PENDING,
    REGISTRATION_STATES.OTP_VERIFYING,
    REGISTRATION_STATES.OTP_ERROR,
    REGISTRATION_STATES.OTP_EXPIRED,
    REGISTRATION_STATES.OTP_RESENDING,
  ].includes(registrationState)

  const showSuccessModal = registrationState === REGISTRATION_STATES.VERIFICATION_SUCCESS

  return (
    <div className="register-responsive-root">
      <div
        className="register-header-section"
        style={{
          position: "absolute",
          top: "0",
          left: "0",
          width: "100%",
          height: "100px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "0 60px",
          zIndex: 10,
          boxSizing: "border-box",
        }}
      >
        {/* Logo */}
        <div
          style={{
            width: '230px',
            height: '90px',
            backgroundImage: "url(/icons/navbar/Sulhafa_Beta_Logo.png)",
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            cursor: "pointer",
          }}
          onClick={() => navigate("/")}
        ></div>

      </div>
      {/* Left Section - Design Panel */}
      <div className="register-left-panel">
        {/* Main Circle with Character */}
        <div
          style={{
            position: "relative",
            width: "540px",
            height: "540px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2,
          }}
        >
          <img
            src={`/images/${normalizedUserType === "pet_guardian"
              ? "register-petowner.png"
              : normalizedUserType === "veterinarian"
                ? "register-vet.png"
                : normalizedUserType === "firm"
                  ? "landing/landing-logo.png"
                  : "register-service.png"
              }`}
            alt={config.alt}
            style={{
              position: "relative",
              width: "540px",
              height: "540px",
              objectFit: "contain",
              zIndex: 2,
              transform: isRTL ? "scaleX(-1)" : "none",
            }}
            onError={(e) => {
              console.error(`Failed to load image: ${e.target.src}`)
              e.target.style.display = "none"
            }}
          />
        </div>
      </div>

      {/* Right Section - Registration Form */}
      <div className="register-right-panel">
        {/* Progress Steps */}
        <div
          className="register-progress-steps"
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              width: "100%",
              maxWidth: "600px",
            }}
          >
            {/* Step 1 - Active */}
            <div
              style={{
                width: "40px",
                height: "40px",
                background: config.primaryColor,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "18px",
                color: "#FFFFFF",
                zIndex: 2,
              }}
            >
              1
            </div>

            {/* Connecting Line 1-2 */}
            <div
              style={{
                flex: 1,
                height: "3px",
                background: "#E5E7EB",
                margin: "0 20px",
              }}
            />

            {/* Step 2 - Inactive */}
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#E5E7EB",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "18px",
                color: "#9CA3AF",
                zIndex: 2,
              }}
            >
              2
            </div>

            {/* Connecting Line 2-3 */}
            <div
              style={{
                flex: 1,
                height: "3px",
                background: "#E5E7EB",
                margin: "0 20px",
              }}
            />

            {/* Step 3 - Inactive */}
            <div
              style={{
                width: "40px",
                height: "40px",
                background: "#E5E7EB",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "18px",
                color: "#9CA3AF",
                zIndex: 2,
              }}
            >
              3
            </div>
          </div>
        </div>

        {/* Form Content Container */}
        <div
          className="register-form-content"
        >
          {/* Form Header */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "48px",
            }}
          >
            <h2
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "20px",
                lineHeight: "1.3",
                color: "#1F2937",
                margin: "0 0 12px 0",
              }}
            >
              {t("register.create")}
            </h2>
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "1.5",
                color: "#6B7280",
                margin: 0,
              }}
            >
              {t("register.petloving")}
            </p>
          </div>

          {/* Error Message */}
          {(errors.general || errors.email || errors.mobile) && (
            <div
              style={{
                padding: "12px",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                borderRadius: "8px",
                marginBottom: "24px",
              }}
            >
              <p
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "12px",
                  color: "#EF4444",
                  margin: 0,
                }}
              >
                {errors.general || errors.email || errors.mobile}
              </p>
            </div>
          )}

          {/* Name Fields */}
          <div
            className="register-name-fields"
            style={{
              display: "flex",
              gap: "20px",
              marginBottom: "32px",
            }}
          >
            <div style={{ flex: 1 }}>
              <label
                style={{
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontWeight: 500,
                  fontSize: "14px",
                  color: "#374151",
                  display: "block",
                  marginBottom: "10px",
                }}
              >
                {normalizedUserType === "firm" ? t("register.name") : t("register.firstname")}
              </label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                placeholder={normalizedUserType === "firm" ? t("register.nameplaceholder") : t("register.firstnameplaceholder")}
                disabled={isFormLoading}
                style={{
                  width: "100%",
                  height: "56px",
                  padding: "0 20px",
                  border: `1.5px solid ${errors.firstName ? "#EF4444" : "#E5E7EB"}`,
                  borderRadius: "14px",
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: "14px",
                  color: "#1F2937",
                  boxSizing: "border-box",
                  outline: "none",
                  transition: "border-color 0.2s ease",
                  background: isFormLoading ? "#F9FAFB" : "#FFFFFF",
                  cursor: isFormLoading ? "not-allowed" : "text",
                }}
                onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                onBlur={(e) =>
                  !isFormLoading && (e.target.style.borderColor = errors.firstName ? "#EF4444" : "#E5E7EB")
                }
              />
              {errors.firstName && (
                <p
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "12px",
                    color: "#EF4444",
                    marginTop: "8px",
                  }}
                >
                  {errors.firstName}
                </p>
              )}
            </div>
            {normalizedUserType !== "firm" && (
              <div style={{ flex: 1 }}>
                <label
                  style={{
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontWeight: 500,
                    fontSize: "14px",
                    color: "#374151",
                    display: "block",
                    marginBottom: "10px",
                  }}
                >
                  {t("register.lastname")}
                </label>
                <input
                  type="text"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  placeholder={t("register.lastnameplaceholder")}
                  disabled={isFormLoading}
                  style={{
                    width: "100%",
                    height: "56px",
                    padding: "0 20px",
                    border: `1.5px solid ${errors.lastName ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "14px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#1F2937",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    background: isFormLoading ? "#F9FAFB" : "#FFFFFF",
                    cursor: isFormLoading ? "not-allowed" : "text",
                  }}
                  onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                  onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.lastName ? "#EF4444" : "#E5E7EB")}
                />
                {errors.lastName && (
                  <p
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "12px",
                      color: "#EF4444",
                      marginTop: "8px",
                    }}
                  >
                    {errors.lastName}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Tab Selector */}
          <div
            style={{
              display: "flex",
              marginBottom: "32px",
              borderBottom: "1px solid #E5E7EB",
            }}
          >
            <button
              onClick={() => handleTabChange("email")}
              disabled={isFormLoading}
              style={{
                flex: 1,
                padding: "16px 0",
                background: "none",
                border: "none",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: activeTab === "email" ? config.primaryColor : "#9CA3AF",
                cursor: isFormLoading ? "not-allowed" : "pointer",
                borderBottom: activeTab === "email" ? `3px solid ${config.primaryColor}` : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              {t("register.email")}
            </button>
            <button
              onClick={() => handleTabChange("mobile")}
              disabled={isFormLoading}
              style={{
                flex: 1,
                padding: "16px 0",
                background: "none",
                border: "none",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: activeTab === "mobile" ? config.primaryColor : "#9CA3AF",
                cursor: isFormLoading ? "not-allowed" : "pointer",
                borderBottom: activeTab === "mobile" ? `3px solid ${config.primaryColor}` : "3px solid transparent",
                transition: "all 0.2s ease",
              }}
            >
              {t("register.mobile")}
            </button>
          </div>

          {/* Contact Input */}
          <div style={{ marginBottom: "32px" }}>
            {activeTab === "email" ? (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                  }}
                >
                  <path
                    d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <polyline
                    points="22,6 12,13 2,6"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  placeholder={t("register.emailplaceholder")}
                  disabled={isFormLoading}
                  style={{
                    width: "100%",
                    height: "56px",
                    padding: "0 20px 0 50px",
                    border: `1.5px solid ${errors.email ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "14px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#1F2937",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    background: isFormLoading ? "#F9FAFB" : "#FFFFFF",
                    cursor: isFormLoading ? "not-allowed" : "text",
                  }}
                  onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                  onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.email ? "#EF4444" : "#E5E7EB")}
                />
                {errors.email && (
                  <p
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "12px",
                      color: "#EF4444",
                      marginTop: "8px",
                    }}
                  >
                    {errors.email}
                  </p>
                )}
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{
                    position: "absolute",
                    left: "16px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    zIndex: 2,
                  }}
                >
                  <path
                    d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
                    stroke="#9CA3AF"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <input
                  type="tel"
                  name="mobile"
                  value={formData.mobile}
                  onChange={handleInputChange}
                  placeholder={t("register.mobileplaceholder")}
                  disabled={isFormLoading}
                  style={{
                    width: "100%",
                    height: "56px",
                    padding: "0 20px 0 50px",
                    border: `1.5px solid ${errors.mobile ? "#EF4444" : "#E5E7EB"}`,
                    borderRadius: "14px",
                    fontFamily: '"Red Hat Display", sans-serif',
                    fontSize: "14px",
                    color: "#1F2937",
                    boxSizing: "border-box",
                    outline: "none",
                    transition: "border-color 0.2s ease",
                    background: isFormLoading ? "#F9FAFB" : "#FFFFFF",
                    cursor: isFormLoading ? "not-allowed" : "text",
                  }}
                  onFocus={(e) => !isFormLoading && (e.target.style.borderColor = config.primaryColor)}
                  onBlur={(e) => !isFormLoading && (e.target.style.borderColor = errors.mobile ? "#EF4444" : "#E5E7EB")}
                />
                {errors.mobile && (
                  <p
                    style={{
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: "12px",
                      color: "#EF4444",
                      marginTop: "8px",
                    }}
                  >
                    {errors.mobile}
                  </p>
                )}
              </div>
            )}
          </div>

          {/* Social Login Section */}
          <div
            style={{
              textAlign: "center",
              marginBottom: "32px",
            }}
          >
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "12px",
                color: "#9CA3AF",
                margin: "0 0 20px 0",
              }}
            >
              {t("register.orsign")}
            </p>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: '12px',
                maxWidth: '280px',
                margin: '0 auto'
              }}
            >
              {/* Google Button */}
              <button
                onClick={() => handleSocialLogin("google")}
                disabled={isFormLoading}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  height: '50px',
                  padding: '0 12px',
                  background: '#FFFFFF',
                  border: '1px solid #303E4E99',
                  borderRadius: '10px',
                  fontFamily: '"Red Hat Display", sans-serif',
                  fontSize: '12px',
                  fontWeight: 600,
                  color: '#374151',
                  cursor: isFormLoading ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: isFormLoading ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isFormLoading) {
                    e.target.style.borderColor = '#D1D5DB'
                    e.target.style.background = '#F9FAFB'
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFormLoading) {
                    e.target.style.borderColor = '#303E4E99'
                    e.target.style.background = '#FFFFFF'
                  }
                }}
              >
                <img
                  src="/icons/google-icon.svg"
                  alt={t("register.social.google")}
                  style={{ width: '20px', height: '20px' }}
                />
                {t("register.social.google")}
              </button>

              {/* Apple Button */}
              {/* Apple Button */}
              <AppleLogin
                clientId={import.meta.env.VITE_APPLE_CLIENT_ID}
                redirectURI={import.meta.env.VITE_APPLE_REDIRECT_URI}
                usePopup={true}
                scope="name email"
                responseMode="query"
                callback={handleAppleSuccess}
                render={renderProps => (
                  <button
                    onClick={renderProps.onClick}
                    disabled={isFormLoading}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                      height: '50px',
                      padding: '0 12px',
                      background: '#FFFFFF',
                      border: '1px solid #303E4E99',
                      borderRadius: '10px',
                      fontFamily: '"Red Hat Display", sans-serif',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#374151',
                      cursor: isFormLoading ? 'not-allowed' : 'pointer',
                      transition: 'all 0.2s ease',
                      opacity: isFormLoading ? 0.6 : 1
                    }}
                    onMouseEnter={(e) => {
                      if (!isFormLoading) {
                        e.target.style.borderColor = '#D1D5DB'
                        e.target.style.background = '#F9FAFB'
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isFormLoading) {
                        e.target.style.borderColor = '#303E4E99'
                        e.target.style.background = '#FFFFFF'
                      }
                    }}
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#000000">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                    </svg>
                    {t("register.social.apple")}
                  </button>
                )}
              />

            </div>
          </div>

          {/* Submit Button */}
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "30px",
            }}
          >
            <button
              onClick={handleSubmit}
              disabled={isFormLoading}
              style={{
                width: "200px",
                height: "45px",
                background: isFormLoading ? "#E5E7EB" : config.primaryColor,
                borderRadius: "14px",
                border: "none",
                fontFamily: '"Red Hat Display", sans-serif',
                fontWeight: 600,
                fontSize: "14px",
                color: isFormLoading ? "#9CA3AF" : "#FFFFFF",
                cursor: isFormLoading ? "not-allowed" : "pointer",
                transition: "all 0.2s ease",
                boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
              onMouseEnter={(e) => {
                if (!isFormLoading) {
                  e.target.style.transform = "translateY(-1px)"
                  e.target.style.boxShadow = "0 6px 20px rgba(0,0,0,0.15)"
                }
              }}
              onMouseLeave={(e) => {
                if (!isFormLoading) {
                  e.target.style.transform = "translateY(0)"
                  e.target.style.boxShadow = "0 4px 12px rgba(0,0,0,0.1)"
                }
              }}
            >
              {isFormLoading && (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  style={{
                    animation: "spin 1s linear infinite",
                  }}
                >
                  <path
                    d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              )}
              {isFormLoading ? "Creating Account..." : "Next"}
              <style>
                {`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}
              </style>
            </button>
          </div>

          {/* Login Link */}
          <div
            style={{
              textAlign: "center",
            }}
          >
            <p
              style={{
                fontFamily: '"Red Hat Display", sans-serif',
                fontSize: "12px",
                color: "#9CA3AF",
                margin: 0,
              }}
            >
              {t("register.alreadyacc")}{" "}
              <span
                onClick={() => !isFormLoading && navigate(`/auth/login?userType=${normalizedUserType}`)}
                style={{
                  color: config.primaryColor,
                  cursor: isFormLoading ? "not-allowed" : "pointer",
                  fontWeight: 500,
                  opacity: isFormLoading ? 0.5 : 1,
                }}
              >
                {t("register.login")}
              </span>
            </p>
          </div>
        </div>
      </div>

      {/* OTP Modal */}
      {showOTPModal && (
        <OTPModal
          isOpen={true}
          primaryColor={config.primaryColor}
          target={otpData.target}
          timeRemaining={timeRemaining}
          attemptsRemaining={otpData.attemptsRemaining}
          canResend={otpData.canResend}
          isVerifying={registrationState === REGISTRATION_STATES.OTP_VERIFYING}
          isResending={registrationState === REGISTRATION_STATES.OTP_RESENDING}
          error={errors.otp}
          onVerify={handleOTPVerify}
          onResend={handleOTPResend}
          onClose={() => {
            // Close the modal by resetting to form filling state
            setRegistrationState(REGISTRATION_STATES.FORM_FILLING)
            setErrors({})
          }}
        />
      )}

      {/* Success Modal */}
      {showSuccessModal && (
        <SuccessModal
          isOpen={true}
          title={t('register.success.title')}
          message={t('register.success.message')}
          buttonText={t('register.success.button')}
          loadingText={t('register.success.loading')}
          target={otpData.target}
          email={formData.email || formData.mobile || ''}
          variant="verification"
          onContinue={handleSuccessContinue}
        />
      )}
      {/* Responsive styles */}
      <style>{`
        .register-responsive-root {
          width: 100vw;
          height: 100vh;
          background: #FAFAFA;
          position: fixed;
          top: 0;
          left: 0;
          font-family: "Red Hat Display", sans-serif;
          overflow: auto;
          display: flex;
        }
        .register-left-panel {
          width: 50%;
          height: 100vh;
          background: #FEFEFA;
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 80px 32px 32px 32px;
          box-sizing: border-box;
          position: relative;
          overflow: hidden;
        }
        .register-right-panel {
          width: 50%;
          height: 100vh;
          background: #FFFFFF;
          box-sizing: border-box;
          position: relative;
          overflow: auto;
          display: flex;
          flex-direction: column;
          justify-content: center;
          padding: 80px 40px 40px 40px;
        }
        .register-progress-steps {
          position: absolute;
          top: 40px;
          left: 0;
          right: 0;
          width: 100%;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 24px;
          box-sizing: border-box;
        }
        .register-form-content {
          width: 100%;
          max-width: 550px;
          margin: 0 auto;
          padding: 0;
          box-sizing: border-box;
        }
        
        /* Hide bubbles on all screens */
        .register-bubble {
          display: none !important;
        }
        
        /* Header hiding */
        .register-header-section {
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100px;
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 0 60px;
          z-index: 10;
          box-sizing: border-box;
        }
        
        @media (max-width: 1024px) {
          .register-header-section {
            padding: 0 40px;
          }
        }
        
        @media (max-width: 1024px) {
          .register-left-panel {
            padding: 60px 24px 24px 24px;
          }
          
          .register-left-panel > div {
            width: 400px !important;
            height: 400px !important;
          }
          
          .register-left-panel img {
            width: 400px !important;
            height: 400px !important;
          }
          
          .register-right-panel {
            padding: 80px 32px 32px 32px !important;
          }
        }
        
        @media (max-width: 768px) {
          .register-responsive-root {
            flex-direction: column;
            padding-top: 0;
          }
          
          .register-header-section {
            display: none !important;
          }
          
          .register-left-panel {
            display: none !important;
          }
          
          .register-right-panel {
            width: 100% !important;
            height: auto !important;
            min-height: 100vh;
            padding: 60px 24px 40px 24px !important;
            overflow: visible !important;
            justify-content: flex-start !important;
          }
          
          .register-progress-steps {
            position: relative !important;
            top: 0 !important;
            margin-bottom: 24px !important;
            padding: 0 !important;
          }
          
          .register-progress-steps > div {
            max-width: 100% !important;
          }
          
          .register-progress-steps > div > div {
            width: 32px !important;
            height: 32px !important;
            font-size: 14px !important;
          }
          
          .register-progress-steps > div > div[style*="margin"] {
            margin: 0 12px !important;
          }
          
          .register-form-content {
            position: relative !important;
            top: 0 !important;
            max-width: 100% !important;
            padding: 0 !important;
          }
          
          /* Stack name fields vertically on mobile */
          .register-name-fields {
            flex-direction: column !important;
            gap: 16px !important;
            margin-bottom: 24px !important;
          }
          
          /* Form header responsive */
          .register-form-content > div:first-child {
            margin-bottom: 32px !important;
          }
          
          .register-form-content > div:first-child h2 {
            font-size: 18px !important;
          }
          
          .register-form-content > div:first-child p {
            font-size: 14px !important;
          }
          
          /* Input fields responsive */
          input[type="text"],
          input[type="email"],
          input[type="tel"] {
            height: 52px !important;
            font-size: 16px !important;
            padding: 0 16px !important;
          }
          
          /* Tab selector responsive */
          .register-form-content > div[style*="borderBottom"] {
            margin-bottom: 24px !important;
          }
          
          .register-form-content > div[style*="borderBottom"] button {
            padding: 14px 0 !important;
            font-size: 14px !important;
          }
          
          /* Social login buttons responsive */
          .register-form-content > div[style*="display: flex"][style*="gap"] {
            flex-direction: column !important;
            gap: 12px !important;
            margin-bottom: 24px !important;
          }
          
          .register-form-content > div[style*="display: flex"][style*="gap"] button {
            width: 100% !important;
            height: 52px !important;
            font-size: 14px !important;
            padding: 0 16px !important;
          }
          
          /* Submit button responsive */
          button[style*="background"][style*="color: #FFFFFF"] {
            width: 100% !important;
            height: 52px !important;
            font-size: 16px !important;
            margin-top: 24px !important;
          }
          
          /* Login link responsive */
          .register-form-content > div[style*="textAlign: center"] {
            margin-top: 24px !important;
            font-size: 14px !important;
          }
        }
        
        @media (max-width: 480px) {
          .register-right-panel {
            padding: 50px 16px 32px 16px !important;
          }
          
          .register-progress-steps {
            margin-bottom: 20px !important;
          }
          
          .register-progress-steps > div > div {
            width: 28px !important;
            height: 28px !important;
            font-size: 12px !important;
          }
          
          .register-progress-steps > div > div[style*="margin"] {
            margin: 0 8px !important;
            height: 2px !important;
          }
          
          .register-form-content > div:first-child {
            margin-bottom: 24px !important;
          }
          
          .register-form-content > div:first-child h2 {
            font-size: 16px !important;
            margin-bottom: 8px !important;
          }
          
          .register-form-content > div:first-child p {
            font-size: 13px !important;
          }
          
          input[type="text"],
          input[type="email"],
          input[type="tel"] {
            height: 48px !important;
            font-size: 16px !important;
            padding: 0 14px !important;
          }
          
          label {
            font-size: 13px !important;
            margin-bottom: 8px !important;
          }
          
          .register-name-fields {
            margin-bottom: 20px !important;
            gap: 12px !important;
          }
          
          .register-form-content > div[style*="borderBottom"] {
            margin-bottom: 20px !important;
          }
          
          .register-form-content > div[style*="borderBottom"] button {
            padding: 12px 0 !important;
            font-size: 13px !important;
          }
          
          .register-form-content > div[style*="display: flex"][style*="gap"] button {
            height: 48px !important;
            font-size: 13px !important;
          }
          
          button[style*="background"][style*="color: #FFFFFF"] {
            height: 48px !important;
            font-size: 15px !important;
            margin-top: 20px !important;
          }
          
          /* Error messages responsive */
          div[style*="background: #FEF2F2"] {
            padding: 10px !important;
            margin-bottom: 20px !important;
          }
          
          div[style*="background: #FEF2F2"] p {
            font-size: 11px !important;
          }
        }
      `}</style>
    </div>
  )
}

export default Register
